import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

export type LifestylePicture = {
  id: number;
  sidekick_uid: string;
  picture_s3_path: string;
  picture_url: string | null;
  created_at: string;
};

const QUERY_KEY = ["sidekick_lifestyle_pictures"];

export function useLifestylePictures() {
  return useQuery({
    queryKey: QUERY_KEY,
    staleTime: 45 * 60 * 1000,
    queryFn: async (): Promise<LifestylePicture[]> => {
      const res = await apiRequest("GET", "/sidekick_profile/me/lifestyle-pictures");
      return res.json();
    },
  });
}

async function getUploadUrl(contentType: string): Promise<{ upload_url: string; object_key: string }> {
  const res = await apiRequest(
    "POST",
    "/sidekick_profile/me/lifestyle-pictures/upload-url",
    { content_type: contentType },
  );
  return res.json();
}

export function useUploadLifestylePicture() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File): Promise<LifestylePicture> => {
      const { upload_url, object_key } = await getUploadUrl(file.type);
      const putRes = await fetch(upload_url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      if (!putRes.ok) {
        throw new Error(`S3 upload failed: ${putRes.status}`);
      }
      const res = await apiRequest("POST", "/sidekick_profile/me/lifestyle-pictures", {
        picture_s3_path: object_key,
      });
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useDeleteLifestylePicture() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/sidekick_profile/me/lifestyle-pictures/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}
